/* eslint-disable no-underscore-dangle, class-methods-use-this */

import _ from 'lodash';
import Formula from '../NumericValues/Formula';
import AttributeConstraintsFormula from './../Attribute/Collection/Formula/index';
import AttributeConstraintValues from './../Attribute/Collection/Values/index';
import AttributeConstraintRanges from './../Attribute/Collection/Ranges/index';
import AttributeEvaluationResponse from './../Attribute/AttributeEvaluationResponse/index';
import FormulaEvaluationException from './../Exceptions/formulaEvaluationException';
import Attribute from '../Attribute';
import Range from '../NumericValues/Range';
import math, { lcm, subtract, add } from '../MathHelpers';

function validateConfiguration(ruleSet) {
  if (_.isNil(ruleSet)) {
    throw new Error('ruleSet must not be null or undefined.');
  }

  return true;
}

function _isRangeValue(value) {
  return _.has(value, 'minimum');
}

function _isFormulaValue(value) {
  let isFormulaValue = false;
  if (!_isRangeValue(value) && !_.isNumber(value)) {
    isFormulaValue = Formula.containsVariables(value);
  }

  return isFormulaValue;
}

function _isSpecificValue(value) {
  return !_isRangeValue(value) && !_isFormulaValue(value);
}

/**
 * Include the given value in the set of valid values, avoiding duplicate entries in the valid value set.
 * @param validValues - The set of valid values.
 * @param value - The value to include.
 * @private
 */
function _includeValue(validValues, value) {
  if (!_.some(validValues, validValue => _.isEqual(validValue, value))) {
    validValues.push(value);
  }
}

/**
 * Exclude the given value from the set of valid values.
 * @param validValues - The set of valid values.
 * @param value - The value to exclude.
 * @private
 */
function _excludeValue(validValues, value) {
  // eslint-disable-next-line no-param-reassign
  validValues = _.remove(validValues, validValue => _.isEqual(validValue, value));
}

/**
 * Calculate the range yielded by intersecting the two provided ranges with the same increment.
 * @param rangeOne - A range to intersect to intersect with the other range.
 * @param rangeTwo - A range to intersect to intersect with the other range.
 * @returns {Range} - A range representing the intersection of the two provided ranges.
 * @private
 */
function _intersectContinuousRanges(rangeOne, rangeTwo) {
  // The maximum of the intersection is always the smallest non-null maximum.
  const intersectedMaximum = _.sortBy([rangeOne.maximum, rangeTwo.maximum])[0];
  const intersectedMinimum = Math.max(rangeOne.minimum, rangeTwo.minimum);

  return new Range(intersectedMinimum, intersectedMaximum, rangeOne.increment, rangeOne.unitOfMeasure);
}

/**
 * Calculate the range yielded by intersecting the two
 * @param rangeOne - A range to intersect with the other range.
 * @param rangeTwo - A range to intersect with the other range.
 * @returns {Range} - A range representing the intersection of the two provided ranges.
 * @private
 */
function _intersectDiscreteRanges(rangeOne, rangeTwo) {
  const intersectedIncrement = lcm(rangeOne.increment, rangeTwo.increment);
  const intersectedMinimum = rangeOne.findMinimalCommonValue(rangeTwo);

  const areInfiniteRanges = !(_.isFinite(rangeOne.maximum) || _.isFinite(rangeTwo.maximum));
  const intersectedMaximum = areInfiniteRanges ? Infinity : math.min(rangeOne.maximum, rangeTwo.maximum);

  return new Range(intersectedMinimum, intersectedMaximum, intersectedIncrement);
}

/**
 * Constrain the currently valid values by intersecting with a rule's set of valid values.
 *
 * Currently, we only handle a subset of all possible intersection cases:
 *  - valid value intersects with rule value
 *  - valid value intersects with rule range
 *  - valid range intersects with rule value
 *  - valid formula intersects with rule formula
 *  - valid continuous range intersects with rule continuous range
 *  - valid discrete range intersects with rule discrete range (same increment)
 * @param values - The set of current valid values to constrain by intersection.
 * @param intersectionValues - The rule's values use as an intersection constraint.
 * @returns {Array} - The new set of valid values with the rule constraint applied.
 * @private
 */
function _intersectValues(values, intersectionValues) {
  const validValues = [];
  _.each(values, (value) => {
    _.each(intersectionValues, (intersectionValue) => {
      if (_isRangeValue(value) && _isRangeValue(intersectionValue)) {
        if (value.isContinuous() && intersectionValue.isContinuous()) {
          if (value.overlaps(intersectionValue)) {
            const intersectedRange = _intersectContinuousRanges(value, intersectionValue);

            _includeValue(validValues, intersectedRange);
          }
        } else if (value.isDiscrete() && intersectionValue.isDiscrete()) {
          if (value.overlaps(intersectionValue)) {
            const intersectedRange = _intersectDiscreteRanges(value, intersectionValue);

            _includeValue(validValues, intersectedRange);
          }
        } else if (value.isDiscrete() && intersectionValue.isImpliable()) {
          if (value.contains(intersectionValue.minimum)) {
            _includeValue(validValues, intersectionValue.minimum);
          }
        } else {
          // we don't support some range/range intersection cases, so assume the valid range remains valid.
          _includeValue(validValues, value);
        }
      } else if (_isFormulaValue(value) && _isFormulaValue(intersectionValue)) {
        if (_.isEqual(value, intersectionValue)) {
          _includeValue(validValues, value);
        }
      } else if (_isSpecificValue(value) && _isSpecificValue(intersectionValue)) {
        if (_.isEqual(value, intersectionValue)) {
          _includeValue(validValues, value);
        }
      } else if (_isRangeValue(value) && _isSpecificValue(intersectionValue)) {
        // specific value intersecting with range -- ranges cannot contain formula or other ranges.
        const rangeValue = value;
        if (rangeValue.contains(intersectionValue)) {
          _includeValue(validValues, intersectionValue);
        }
      } else if (_isRangeValue(value) && _isFormulaValue(intersectionValue)) {
        /**
         * We don't support intersecting a formula against a valid range, so assume both are valid.
         * We'll need to evaluate the formula later.
         */
        _includeValue(validValues, value);
        _includeValue(validValues, intersectionValue);
      } else if (_isSpecificValue(value) && _isRangeValue(intersectionValue)) {
        // range intersecting with specific value -- ranges cannot contain formula or other ranges.
        const rangeIntersectionValue = intersectionValue;
        if (rangeIntersectionValue.contains(value)) {
          _includeValue(validValues, value);
        }
      } else if (_isFormulaValue(value) && _isRangeValue(intersectionValue)) {
        // we don't support intersecting a range against a valid formula, so assume the formula is valid.
        _includeValue(validValues, value);
      } else if (_isFormulaValue(value) && _isSpecificValue(intersectionValue)) {
        // we don't support intersecting a specific value against a valid formula, so assume the formula remains valid.
        _includeValue(validValues, value);
      } else if (_isSpecificValue(value) && _isFormulaValue(intersectionValue)) {
        // we don't support intersecting an unevaluated formula against a valid specific value, so assume the specific value remains valid.
        _includeValue(validValues, value);
      }
    });
  });

  return validValues;
}

/**
 * Constrain the continuous valid range by excluding values in the continuous exclusion range.
 * @param validRange - the valid range to constrain by exclusion.
 * @param exclusionRange - the range to exclude from the valid range.
 * @returns {Array} - the range(s) yielded by excluding values in the exclusion range.
 * @private
 */
function _excludeContinuousRanges(validRange, exclusionRange) {
  const includedRanges = [];
  if (!validRange.isImpliable()) {
    if (validRange.hasEqualBoundaries(exclusionRange) || exclusionRange.containsRange(validRange)) {
      /**
       *  Case 1 - exclusion contains the entire valid range
       *
       *  valid range:    |----|         -OR-   |------|   -OR-  |------|      -OR-      |------|
       *  exclusion:   |-------------|          |------|         |---------|          |---------|
       */
      // do nothing, the exclusion has removed the entire valid range.
    } else if (validRange.containsRange(exclusionRange)) {
      /**
       *  Case 2 - exclusion contained within valid range
       *
       *  valid range:        |------------------------|
       *  exclusion:                  |--------|
       */
      if (validRange.minimum !== exclusionRange.minimum) {
        const leftIncludedMaximum = subtract(exclusionRange.minimum, Range.getSmallestPossibleIncrement());
        const leftIncludedRange = new Range(validRange.minimum, leftIncludedMaximum, undefined, validRange.unitOfMeasure);

        includedRanges.push(leftIncludedRange);
      }

      if (validRange.maximum !== exclusionRange.maximum) {
        const rightIncludedMinimum = add(exclusionRange.maximum, Range.getSmallestPossibleIncrement());
        const rightIncludedRange = new Range(rightIncludedMinimum, validRange.maximum, undefined, validRange.unitOfMeasure);

        includedRanges.push(rightIncludedRange);
      }
    } else {
      /**
       *  Case 3: exclusion contains part of the valid range
       *
       *  valid range:         |-------|  -OR-  |---------|
       *  exclusion:       |---------|               |-----------|
       */
      let includedMinimum;
      let includedMaximum;
      if (validRange.minimum < exclusionRange.minimum) {
        includedMinimum = validRange.minimum;
        includedMaximum = subtract(exclusionRange.minimum, Range.getSmallestPossibleIncrement()); // decrement to properly apply exclusion
      } else {
        includedMinimum = add(exclusionRange.maximum, Range.getSmallestPossibleIncrement()); // increment to properly apply exclusion
        includedMaximum = validRange.maximum;
      }

      const includedRange = new Range(includedMinimum, includedMaximum, undefined, validRange.unitOfMeasure);
      includedRanges.push(includedRange);
    }
  }

  return includedRanges;
}

/**
 * Generate ranges from exclusion of the intersected range onto valid range using a Leap
 * Frog method. Each range is create using the intersected range's increment with a starting
 * ending point based on the valid range.
 *
 *  valid range:        |---|---|---|---|---|---|---|---|
 *  intersection:           |-----------|-----------|
 *  new range 1:                |-----------|
 *  new range 2:                    |-----------|
 */
function _createLeapFrogRanges(validRange, intersectedRange, includedRanges) {
  let currentIntersectedValue = add(intersectedRange.minimum, validRange.increment);

  while (currentIntersectedValue < add(intersectedRange.minimum, intersectedRange.increment)) {
    const includedRangeMinimum = currentIntersectedValue;
    const includedRangeMaximum = intersectedRange.maximum;
    const includedRangeIncrement = intersectedRange.increment;

    const includedMiddleRange = new Range(includedRangeMinimum, includedRangeMaximum, includedRangeIncrement, validRange.unitOfMeasure);

    includedRanges.push(includedMiddleRange);

    currentIntersectedValue = add(currentIntersectedValue, validRange.increment);
  }
}

/**
 * Generate ranges from exclusion of the intersected range onto valid range using a Line
 * Range method. Each range is created using the valid range's increment with starting and
 * ending points based on the intersected range's incremental sections.
 *
 *  valid range:        |---|---|---|---|---|---|---|---|
 *  intersection:           |-----------|-----------|
 *  new range 1:                |---|
 *  new range 2:                            |---|
 */
function _createLineRanges(validRange, intersectedRange, includedRanges) {
  let currentIntersectedValue = intersectedRange.minimum;

  while (currentIntersectedValue <= intersectedRange.maximum) {
    const includedRangeMinimum = add(currentIntersectedValue, validRange.increment);
    const includedRangeMaximum = subtract(add(currentIntersectedValue, intersectedRange.increment), validRange.increment);
    const includedRangeIncrement = validRange.increment;

    const includedMiddleRange = new Range(includedRangeMinimum, includedRangeMaximum, includedRangeIncrement, validRange.unitOfMeasure);

    includedRanges.push(includedMiddleRange);

    currentIntersectedValue = add(currentIntersectedValue, intersectedRange.increment);
  }
}

/**
 * Constrain the discrete valid range by excluding values in the discrete exclusion range.
 * The valid range's increment must be equal the exclusion range's increment.
 * @param validRange - the valid range to constrain by exclusion.
 * @param exclusionRange - the range to exclude from the valid range.
 * @returns {Array} - the range(s) yielded by excluding values in the exclusion range.
 * @private
 */
function _excludeDiscreteRanges(validRange, exclusionRange) {
  const includedRanges = [];
  if (!validRange.isImpliable()) {
    /**
     *  Case 2.1 - discrete range excludes equal discrete range
     *
     *  valid range:   |---------|
     *  exclusion:     |---------|
     *
     *  Do nothing, the exclusion has removed the entire valid range.
     */
    /**
     *  Case 2.2 - discrete range exclusion contained within valid range
     *
     *  valid range:        |------------------------|
     *  exclusion:                  |--------|
     */
    /**
     *  Case 2.3 - valid range contained within discrete range exclusion
     *
     *  valid range:                |--------|
     *  exclusion:          |------------------------|
     */
    /**
     *  Case 2.4 - discrete range exclusion contains part of the valid range
     *
     *  valid range:         |-------|  -OR-  |---------|
     *  exclusion:       |---------|               |-----------|
     */

    // Find the intersected values and exclude those from the valid range
    const intersectedRange = _intersectDiscreteRanges(validRange, exclusionRange);

    if (intersectedRange.minimum > validRange.minimum) {
      const includedRangeMinimum = validRange.minimum;
      const includedRangeMaximum = subtract(intersectedRange.minimum, validRange.increment);

      const includedLowerRange = new Range(includedRangeMinimum, includedRangeMaximum, validRange.increment, validRange.unitOfMeasure);

      includedRanges.push(includedLowerRange);
    }

    if (intersectedRange.maximum < validRange.maximum) {
      const includedRangeMinimum = add(intersectedRange.maximum, validRange.increment);
      const includedRangeMaximum = validRange.maximum;

      const includedUpperRange = new Range(includedRangeMinimum, includedRangeMaximum, validRange.increment, validRange.unitOfMeasure);

      includedRanges.push(includedUpperRange);
    }

    /**
     *  If the intersection's increment isn't equal to the valid range's increment then there are
     *  specific values inside the overlap that need to be excluded.
     */
    if (!_.isNil(intersectedRange.increment) && intersectedRange.increment !== validRange.increment) {
      /**
       * When the intersected range in infinite we need to create new ranges that are infinite so
       * create leap frog ranges.
       */
      if (!_.isFinite(intersectedRange.maximum)) {
        _createLeapFrogRanges(validRange, intersectedRange, includedRanges);
      } else {
        /**
         * With finite ranges determine which type of range creation will result in a smaller set of ranges.
         */
        const numberOfNewIncrementalSteps = subtract(intersectedRange.maximum, intersectedRange.minimum) / intersectedRange.increment;
        const inBetweenSteps = subtract(intersectedRange.increment / validRange.increment, 1);

        if (inBetweenSteps > numberOfNewIncrementalSteps) {
          _createLineRanges(validRange, intersectedRange, includedRanges);
        } else {
          _createLeapFrogRanges(validRange, intersectedRange, includedRanges);
        }
      }
    }
  }

  return includedRanges;
}

/**
 * Constrain the currently valid values by excluding a rule's set of valid values.
 *
 * Currently, we only handle a subset of all possible exclusion cases:
 *  - valid value excluded by rule value
 *  - valid value excluded by rule range
 *  - valid formula excluded by rule formula
 *  - valid continuous range excluded by rule continuous range
 *  - valid discrete range excluded by rule discrete range (same increment)
 * @param values - The set of current valid values to constrain by exclusion.
 * @param exclusionValues - A rule's values to exclude from the valid values.
 * @returns {Array} - The new set of valid values with the rule constraint applied.
 * @private
 */
function _excludeValues(values, exclusionValues) {
  const validValues = _.cloneDeep(values);
  _.each(exclusionValues, (exclusionValue) => {
    let valueRunListIndex = 0;
    while (valueRunListIndex < validValues.length) {
      const value = validValues[valueRunListIndex];

      if (_isRangeValue(value) && _isRangeValue(exclusionValue)) {
        if (value.isContinuous() && exclusionValue.isContinuous()) {
          if (value.overlaps(exclusionValue)) {
            const rangesWithRangeExcluded = _excludeContinuousRanges(value, exclusionValue);

            _excludeValue(validValues, value);
            valueRunListIndex -= 1; // decrement by 1 to account for removing the current value.

            rangesWithRangeExcluded.forEach(validRange => _includeValue(validValues, validRange));
          }
        } else if (value.isDiscrete() && exclusionValue.isDiscrete()) {
          if (value.overlaps(exclusionValue)) {
            const rangesWithRangeExcluded = _excludeDiscreteRanges(value, exclusionValue);

            _excludeValue(validValues, value);
            valueRunListIndex -= 1; // decrement by 1 to account for removing the current value.

            rangesWithRangeExcluded.forEach(validRange => _includeValue(validValues, validRange));
          }
        } else {
          // we don't support some range/range exclusion cases, so assume the valid range remains valid.
        }
      } else if (_isFormulaValue(value) && _isFormulaValue(exclusionValue)) {
        if (_.isEqual(value, exclusionValue)) {
          _excludeValue(validValues, exclusionValue);
          valueRunListIndex -= 1; // decrement by 1 to account for removing the current value.
        }
      } else if (_isSpecificValue(value) && _isSpecificValue(exclusionValue)) {
        if (_.isEqual(value, exclusionValue)) {
          _excludeValue(validValues, exclusionValue);
          valueRunListIndex -= 1; // decrement by 1 to account for removing the current value.
        }
      } else if (_isRangeValue(value) && _isSpecificValue(exclusionValue)) {
        // we don't support excluding a specific value from a range yet, so we don't exclude the range value.
      } else if (_isRangeValue(value) && _isFormulaValue(exclusionValue)) {
        // we don't support excluding a formula value from a range, so we don't exclude the range value.
      } else if (_isSpecificValue(value) && _isRangeValue(exclusionValue)) {
        // range excluding specific value -- ranges cannot contain formula or other ranges.
        const rangeExclusionValue = exclusionValue;
        if (rangeExclusionValue.contains(value)) {
          _excludeValue(validValues, value);
          valueRunListIndex -= 1; // decrement by 1 to account for removing the current value.
        }
      } else if (_isFormulaValue(value) && _isRangeValue(exclusionValue)) {
        // we don't support excluding a range from a formula value, so we don't exclude the formula.
      }

      valueRunListIndex += 1;
    }
  });

  return validValues;
}

/**
 * Names for math-evaluation-helper exceptions.
 * Ideally these would be pulled out of math-evaluation-helper but it doesn't expose the types nicely.
 */
const INVALID_CONTEXT_EXCEPTION_NAME = 'InvalidContextException';
const UNKNOWN_STRING_EXCEPTION_NAME = 'UnknownStringException';

module.exports = class Engine {
  /**
   * @param  {RuleSet} ruleSet The rule set to build this engine for
   * @return {Engine}
   */
  constructor(ruleSet) {
    validateConfiguration(ruleSet);

    this._ruleSet = ruleSet;
  }

  get whiteList() {
    return this._ruleSet.whiteList;
  }

  get rules() {
    return this._ruleSet.rules;
  }

  validate(attributeSelection) {
    const calculationExceptions = [];

    /**
     * The attribute domain is an object relating each attribute key to an array of possible attribute values.
     * As each attribute is validate and rules are executed, we modify this object with respect to rule constraints,
     * attribute translations (decontainering), and attribute formulas.
     */
    const attributeDomain = this._createAttributeDomain(attributeSelection, calculationExceptions);

    /**
     * The remaining attribute domain is the same as the primary attribute domain except it keeps track of all
     * possible attribute selections which are valid with respect to all active rules. Rules are activated by
     * evaluating the primary attribute domain then the rule constraints are applied to both the primary attribute domain
     * and the remaining attribute domain.
     * This methodology produces a _mostly_ accurate set of remainingOptions. True accuracy would require capabilities not
     * present in this iteration of the Product Rules Engine.
     */
    const remainingAttributeDomain = this._createRemainingAttributeDomain();

    /**
     * The attribute run list defines the attributes to validate as an array, allowing us to control iteration
     * order as we modify the attribute domain object -- objects do not guarantee iteration order whereas arrays do.
     */
    const attributeRunList = _.keysIn(attributeDomain);

    let runListIndex = 0;
    while (runListIndex < attributeRunList.length) {
      const attributeRules = this._getRulesUsingAttribute(attributeRunList[runListIndex]);

      _.each(attributeRules, (attributeRule) => {
        attributeRule.evaluate(attributeDomain);

        // if the rule is now active, then apply the rule constraints to the attribute domain.
        if (attributeRule.active && !attributeRule.evaluateState) {
          // Apply rule to attributeDomain, enqueue any new attributes to the attributeRunList.
          this._applyActiveRule(attributeRule, attributeDomain, attributeRunList, calculationExceptions);

          // Apply the rule's constraints to the domain of remaining possible attribute selections.
          this._applyActiveRule(attributeRule, remainingAttributeDomain, [], []);
        }
      });

      runListIndex += 1;
    }

    this._calculateAttributeDomainFormulas(attributeDomain, calculationExceptions);

    const response = {};
    response.activeExclusions = _(this.rules)
      .filter(rule => rule.active)
      .map(rule => rule.name)
      .value();
    response.exceptions = calculationExceptions;
    response.finalSelections = this._createFinalSelections(attributeDomain, attributeSelection);
    response.missingAttributes = this._createMissingAttributes(attributeDomain);
    response.remainingOptions = this._createRemainingOptions(remainingAttributeDomain);

    response.violatedExclusions = _(this.rules)
      .filter(rule => rule.evaluateState)
      .map(rule => rule.name)
      .value();
    response.violatedSelections = this._createViolatedSelections(attributeDomain, attributeSelection);

    response.isValid = _.isEmpty(response.violatedExclusions) && _.isEmpty(response.violatedSelections);
    response.isComplete = _.isEmpty(response.missingAttributes);
    if (!response.isValid) {
      response.remainingOptions = null;
    }

    return response;
  }

  /**
   * Get the unit of measure for a white list attribute. There is no top-level unit of measure
   * on a white list attribute, instead we must inspect each white listed value to get the
   * unit of measure. For listOfValues and listOfRanges, we use the unitOfMeasure on the 1st
   * white listed value. For formula, we provide null because there is no unitOfMeasure available.
   * @param {*} whiteListAttribute The white list attribute.
   */
  _getAttributeUnitOfMeasure(whiteListAttribute) {
    let unitOfMeasure = null;
    if (whiteListAttribute._type === Attribute.Types.listOfValues) {
      const representativeAttributeValue = _.first(whiteListAttribute._attributeValues);

      ({ unitOfMeasure } = representativeAttributeValue);
    } else if (whiteListAttribute._type === Attribute.Types.listOfRanges) {
      const representativeAttributeRange = _.first(whiteListAttribute._attributeRanges);

      ({ unitOfMeasure } = representativeAttributeRange);
    }

    return unitOfMeasure;
  }

  _getCanonicalAttributeDefinition(key) {
    let canonicalAttribute = null;
    const whiteListAttribute = this.whiteList.getAttribute(key);
    if (_.isNil(whiteListAttribute)) {
      canonicalAttribute = this._getCanonicalContaineredAttributeDefinition(key);
    } else {
      const whiteListAttributeUnitOfMeasure = this._getAttributeUnitOfMeasure(whiteListAttribute);

      canonicalAttribute = {
        key: whiteListAttribute.key,
        unitOfMeasure: whiteListAttributeUnitOfMeasure,
        class: whiteListAttribute.class,
        subclass: whiteListAttribute.subclass,
        includeOnOrder: whiteListAttribute.includeOnOrder,
        values: whiteListAttribute.validOptions || whiteListAttribute.values,
      };
    }

    return canonicalAttribute;
  }

  _getCanonicalContaineredAttributeDefinition(key) {
    let canonicalAttribute = null;
    _.each(this.whiteList.getAttributes(), (whiteListAttribute) => {
      _.each(whiteListAttribute.attributeValueTranslations, (attributeValueTranslation) => {
        _.each(attributeValueTranslation.attributeSelections, (containeredAttributeSelection) => {
          if (containeredAttributeSelection.attributeKey.toLowerCase() === key.toLowerCase()) {
            canonicalAttribute = {
              key: containeredAttributeSelection.attributeKey,
              class: containeredAttributeSelection.class,
              subclass: containeredAttributeSelection.subclass,
              includeOnOrder: whiteListAttribute.includeOnOrder,
            };
          }
        });
      });
    });

    return canonicalAttribute;
  }

  _createFinalSelections(attributeDomain, attributeSelection) {
    // use the whitelist to normalize final selection keys to the white list's canonical casing.
    const finalSelections = [];
    _.forOwn(attributeDomain, (values, key) => {
      if (values.length === 1) {
        let attributeFinalSelections = [];

        const selectedValue = values[0];
        if (!_isFormulaValue(selectedValue)) {
          if (_isRangeValue(selectedValue) && this._isImpliableRangeValue(selectedValue)) {
            const impliedRangeSelection = selectedValue.minimum;

            attributeFinalSelections = this._createAttributeFinalSelections(key, impliedRangeSelection, attributeSelection);
          } else if (_isSpecificValue(selectedValue)) {
            attributeFinalSelections = this._createAttributeFinalSelections(key, selectedValue, attributeSelection);
          }
        }

        finalSelections.push(...attributeFinalSelections);
      }
    });

    return finalSelections;
  }

  _createAttributeFinalSelections(key, selectedValue) {
    const finalSelections = [];

    /**
     * If an attribute has no canonical definition, then that means the engine has been given
     * an undefined attribute in the attribute selection (i.e. not defined within the white list).
     *
     * Ideally we would strip undefined attributes from the selection before running the engine.
     * Unfortunately, we supported using undefined attributes in formula in the past so we can't
     * pull the rug out from under our existing consumers -- instead, we need to handle undefined attributes
     * here.
     *
     * In the future, we'll revisit our treatment of undefined attributes in the attribute selection -- either
     * reject them outright or strip them out before engine evaluation. For now, we null check to preserve existing
     * behavior.
     */
    const canonicalAttributeDefinition = this._getCanonicalAttributeDefinition(key);
    if (canonicalAttributeDefinition !== null) {
      // Normalize casing for attribute value on output
      const canonicalAttributeValue =
        canonicalAttributeDefinition.values.find((value) => {
          if (value instanceof Range) {
            return false;
          }

          return value.toLowerCase() === selectedValue.toLowerCase();
        }) || selectedValue;

      const finalSelection = {
        attributeValue: canonicalAttributeValue,
        unitOfMeasure: canonicalAttributeDefinition.unitOfMeasure,
        attributeKey: canonicalAttributeDefinition.key,
        class: canonicalAttributeDefinition.class,
        subclass: canonicalAttributeDefinition.subclass,
      };

      finalSelections.push(finalSelection);
      if (canonicalAttributeDefinition.includeOnOrder && canonicalAttributeDefinition.class !== Attribute.Classes.tenant) {
        const includeOnOrderFinalSelection = {
          attributeValue: canonicalAttributeValue,
          unitOfMeasure: canonicalAttributeDefinition.unitOfMeasure,
          attributeKey: canonicalAttributeDefinition.key,
          class: Attribute.Classes.tenant,
        };

        finalSelections.push(includeOnOrderFinalSelection);
      }
    }

    return finalSelections;
  }

  _isImpliableRangeValue(value) {
    let isImpliableRange;
    if (_.isNil(value.maximum)) {
      isImpliableRange = false;
    } else if (_.isNil(value.increment) && value.minimum === value.maximum) {
      isImpliableRange = true;
    } else {
      isImpliableRange = value.minimum + value.increment > value.maximum;
    }

    return isImpliableRange;
  }

  _createViolatedSelections(attributeDomain, attributeSelection) {
    const selectedAttributes = this._getSelectedAttributes(attributeDomain);
    const violatedSelections = this.whiteList.identifyInvalidSelections(selectedAttributes);

    // Enforce consistent attribute key casing using the canonical attribute definition.
    _.each(violatedSelections, (violatedSelection) => {
      const canonicalAttributeDefinition = this._getCanonicalAttributeDefinition(violatedSelection.attributeKey, attributeSelection);

      // eslint-disable-next-line no-param-reassign
      violatedSelection.attributeKey = canonicalAttributeDefinition.key;
    });

    return violatedSelections;
  }

  _createRemainingOptions(attributeDomain) {
    const remainingOptions = [];

    _.each(this.whiteList.getAttributes(), (whiteListAttribute) => {
      const attributeValues = [];
      const attributeRanges = [];

      const validWhiteListAttributeValues = attributeDomain[whiteListAttribute._key.toLowerCase()];
      if (!_.isEmpty(validWhiteListAttributeValues)) {
        if (whiteListAttribute._type === Attribute.Types.listOfValues) {
          const validValues = _.map(validWhiteListAttributeValues, this._createRemainingValidValue);
          attributeValues.push(...validValues);
        } else if (whiteListAttribute._type === Attribute.Types.listOfRanges) {
          const validRanges = _(validWhiteListAttributeValues)
            .filter(value => _isRangeValue(value) || _isSpecificValue(value))
            .map(this._createRemainingValidRange)
            .value();

          attributeRanges.push(...validRanges);
        }
      }

      if (!_.isEmpty(attributeValues) || !_.isEmpty(attributeRanges)) {
        const remainingOption = {
          type: whiteListAttribute._type,
          required: whiteListAttribute._required,
          derived: whiteListAttribute._derived,
          attributeKey: whiteListAttribute._key,
          attributeValues,
          attributeRanges,
        };

        remainingOptions.push(remainingOption);
      }
    });

    return remainingOptions;
  }

  _createRemainingValidRange(validValue) {
    let remainingValidRange;
    if (_isRangeValue(validValue)) {
      remainingValidRange = validValue;
    } else if (_isSpecificValue(validValue)) {
      // Convert specific values into an implied range so it conforms with the rest of attributeRanges.
      const validNumber = _.toNumber(validValue);
      remainingValidRange = new Range(validNumber, validNumber);
    }

    // Convert infinite maximums to undefined so that we stay consistent
    if (!_.isFinite(remainingValidRange.maximum)) {
      remainingValidRange.maximum = undefined;
    }

    return remainingValidRange;
  }

  _createRemainingValidValue(validValue) {
    return { value: validValue };
  }

  _createMissingAttributes(attributeDomain) {
    const selectedAttributes = this._getSelectedAttributes(attributeDomain);
    const missingRequiredAttributes = this.whiteList.identifyMissingRequiredAttributes(selectedAttributes);

    const missingAttributes = _.map(missingRequiredAttributes, (missingRequiredAttribute) => {
      const missingAttribute = {
        attributeKey: missingRequiredAttribute.key,
        required: missingRequiredAttribute.required,
        derived: missingRequiredAttribute.derived,
        class: missingRequiredAttribute.class,
      };

      const validMissingAttributeDomain = attributeDomain[missingRequiredAttribute.key.toLowerCase()];

      if (missingRequiredAttribute instanceof AttributeConstraintValues) {
        missingAttribute.type = Attribute.Types.listOfValues;
        missingAttribute.attributeValues = _.map(validMissingAttributeDomain, validMissingAttributeValue => ({
          value: validMissingAttributeValue,
        }));
      } else if (missingRequiredAttribute instanceof AttributeConstraintRanges) {
        missingAttribute.type = Attribute.Types.listOfRanges;
        missingAttribute.attributeRanges = missingRequiredAttribute._attributeRanges;
      }

      return missingAttribute;
    });

    return missingAttributes;
  }

  /**
   * Replace any values that are unspecified (undefined or not narrowed down to one value) in the attributeDomain
   * with the given values
   * NOTE: This is currently the method used for decontainered attributes as to not clobber existing selections
   * @param attributeDomain The current attributeDomain
   * @param replacementAttributeDomain The attribute domain to assign into the original attribute domain.
   */
  _replaceUnspecifiedAttributeValues(attributeDomain, replacementAttributeDomain) {
    _.each(replacementAttributeDomain, (value, key) => {
      if (_.isNil(attributeDomain[key]) || attributeDomain[key].length > 1) {
        attributeDomain[key] = value; // eslint-disable-line no-param-reassign
      }
    });
  }

  _createAttributeDomain(attributeSelection, calculationExceptions) {
    const attributeDomain = {};

    /**
     * Initialize attribute domain from the whitelist, restricting whitelist values w.r.t the selected attributes
     * copy each attr from whiteList into an object of attribute key to the array of valid values -- { "key" -> [val1, val2, val3, ...], }
     * iterate over attributeSelection -- for each selected attribute, reduce the run list's corresponding array of valid values down to a
     * single element list containing the selected value.
     */
    const normalizedAttributeSelection = _.mapKeys(attributeSelection, (value, key) => key.toLowerCase());
    _.each(this.whiteList.getAttributes(), (whiteListAttribute) => {
      const normalizedAttributeKey = whiteListAttribute.key.toLowerCase();

      // if the whitelisted attribute was selected, then the selected value is the only array value.
      if (this._isAttributeSelected(normalizedAttributeSelection, normalizedAttributeKey)) {
        const selectedAttributeValue = normalizedAttributeSelection[normalizedAttributeKey];

        attributeDomain[normalizedAttributeKey] = [selectedAttributeValue];
      } else if (whiteListAttribute instanceof AttributeConstraintsFormula) {
        // if the whitelisted attribute is a 'formula', then the formula is the only array value.
        const normalizedAttributeFormula = whiteListAttribute.formula.toLowerCase();

        attributeDomain[normalizedAttributeKey] = [normalizedAttributeFormula];
      } else if (whiteListAttribute instanceof AttributeConstraintValues) {
        // if the whitelisted attribute is a 'list of values', then the list are the array values.
        const attributeValues = _.map(whiteListAttribute.values, whiteListValue => whiteListValue.value);

        attributeDomain[normalizedAttributeKey] = attributeValues;
      } else if (whiteListAttribute instanceof AttributeConstraintRanges) {
        attributeDomain[normalizedAttributeKey] = whiteListAttribute.values;
      }
    });

    /* decontainer any selected attributes in the run list */
    _.forOwn(attributeDomain, (value, key) => {
      // only decontainer a selected attribute -- an attribute with a single valid value in the attribute domain.
      if (value.length === 1) {
        const decontaineredAttributeDomain = this._decontainerAttribute(key, value);
        this._replaceUnspecifiedAttributeValues(attributeDomain, decontaineredAttributeDomain);
      }
    });

    this._implyRangeAttributes(attributeDomain);

    const nonWhiteListAttributeSelection = this._getNonWhiteListAttributeSelection(attributeSelection);
    _.merge(attributeDomain, nonWhiteListAttributeSelection);

    /* calculate the attribute domain's formula attributes */
    this._calculateAttributeDomainFormulas(attributeDomain, calculationExceptions);

    return attributeDomain;
  }

  _getNonWhiteListAttributeSelection(attributeSelection) {
    const nonWhiteListAttributeSelection = {};

    _.forOwn(attributeSelection, (value, key) => {
      const normalizedAttributeSelectionKey = key.toLowerCase();
      if (_.isNil(this.whiteList.getAttribute(normalizedAttributeSelectionKey))) {
        nonWhiteListAttributeSelection[normalizedAttributeSelectionKey] = [value];
      }
    });

    return nonWhiteListAttributeSelection;
  }

  _isImpliableRangeValueSet(ranges) {
    const impliedRangeValues = [];

    const allImpliable = _.every(ranges, (range) => {
      const isImpliable = this._isImpliableRangeValue(range);

      if (isImpliable) {
        const impliedValue = range.minimum;
        impliedRangeValues.push(impliedValue);
      }

      return isImpliable;
    });

    return allImpliable && _.uniq(impliedRangeValues).length === 1;
  }

  _createRemainingAttributeDomain() {
    const attributeDomain = {};

    /**
     * Initialize attribute domain from the whitelist, restricting whitelist values w.r.t the selected attributes
     * copy each attr from whiteList into an object of attribute key to the array of valid values -- { "key" -> [val1, val2, val3, ...], }
     * iterate over attributeSelection -- for each selected attribute, reduce the run list's corresponding array of valid values down to a
     * single element list containing the selected value.
     */
    _.each(this.whiteList.getAttributes(), (whiteListAttribute) => {
      const normalizedAttributeKey = whiteListAttribute.key.toLowerCase();

      if (whiteListAttribute instanceof AttributeConstraintsFormula) {
        // if the whitelisted attribute is a 'formula', then the formula is the only array value.
        const normalizedAttributeFormula = whiteListAttribute.formula.toLowerCase();

        attributeDomain[normalizedAttributeKey] = [normalizedAttributeFormula];
      } else if (whiteListAttribute instanceof AttributeConstraintValues) {
        // if the whitelisted attribute is a 'list of values', then the list are the array values.
        const attributeValues = _.map(whiteListAttribute.values, whiteListValue => whiteListValue.value);

        attributeDomain[normalizedAttributeKey] = attributeValues;
      } else if (whiteListAttribute instanceof AttributeConstraintRanges) {
        attributeDomain[normalizedAttributeKey] = whiteListAttribute.values;
      }
    });

    /* decontainer any selected attributes in the run list */
    _.forOwn(attributeDomain, (value, key) => {
      // only decontainer a selected attribute -- an attribute with a single valid value in the attribute domain.
      if (value.length === 1) {
        const decontaineredAttributeDomain = this._decontainerAttribute(key, value);
        this._replaceUnspecifiedAttributeValues(attributeDomain, decontaineredAttributeDomain);
      }
    });

    this._implyRangeAttributes(attributeDomain);

    /* calculate the attribute domain's formula attributes */
    this._calculateAttributeDomainFormulas(attributeDomain, []);

    return attributeDomain;
  }

  _isAttributeSelected(attributeSelection, attributeKey) {
    return _.has(attributeSelection, attributeKey);
  }

  _decontainerAttribute(attributeKey, attributeValue) {
    let translatedAttributeDomain = {};

    // decontainer the attribute from the white list's translations.
    const whiteListAttribute = this.whiteList.getAttribute(attributeKey);
    if (!_.isNil(whiteListAttribute)) {
      translatedAttributeDomain = this._getTranslatedAttributeDomain(attributeValue, whiteListAttribute);
    }

    return translatedAttributeDomain;
  }

  /**
   * Gets the translated attribute domain for an attribute value.
   * @param {*} attributeValue The value whose translations we're retrieving.
   * @param {*} whiteListAttribute The whitelisted attribute whose value is being translated.
   */
  _getTranslatedAttributeDomain(attributeValue, whiteListAttribute) {
    // a selected value may be given as a number, so we must coerce it into a string.
    const normalizedAttributeValue = attributeValue.toString();

    const attributeTranslation = _.find(
      whiteListAttribute.attributeValueTranslations,
      attributeValueTranslation => attributeValueTranslation.attributeValue.value === normalizedAttributeValue,
    );

    const translatedAttributeDomain = {};
    if (!_.isNil(attributeTranslation)) {
      _.each(attributeTranslation.attributeSelections, (translatedAttributeSelection) => {
        translatedAttributeDomain[translatedAttributeSelection.attributeKey.toLowerCase()] = [translatedAttributeSelection.attributeValue];
      });
    }

    return translatedAttributeDomain;
  }

  _calculateAttributeDomainFormulas(attributeDomain, calculationExceptions) {
    _.forOwn(attributeDomain, (value, key) => {
      /**
       * Naively assume any attribute with a single element domain is a formula and evaluate it.
       * Since selected attributes also have single element domains, we'll end up calculating those too -- the engine will throw
       * UNKNOWN_STRING_EXCEPTION_NAME exceptions. We could do some simple formula detection on the value but we may miss some
       * operators, etc -- we'd prefer to swallow the UNKNOWN_STRING_EXCEPTION_NAME exceptions and no-op in this case. We suspect
       * suppressing UNKNOWN_STRING_EXCEPTION_NAME exceptions is safer than missing some formula operators. In the future, we'd handle this
       * by flagging the attribute type on each attribute domain value or creating a better formula string detector.
       */
      if (value.length === 1) {
        const formulaLikeAttributeValue = value[0];
        if (_isFormulaValue(formulaLikeAttributeValue)) {
          const formula = formulaLikeAttributeValue;
          const selectedAttributeDomain = this._getSelectedAttributes(attributeDomain);

          const evaluatedFormula = this._calculateFormula(key, formula, selectedAttributeDomain);
          if (evaluatedFormula._result) {
            if (!_.isUndefined(evaluatedFormula.formula.value)) {
              // overwrite the formula in the attribute domain with the calculated value.
              attributeDomain[key] = [evaluatedFormula.formula.value.toString()]; // eslint-disable-line no-param-reassign
            }
          } else if (!_.isUndefined(evaluatedFormula.exception)) {
            const calculationException = { key: evaluatedFormula.key, exception: evaluatedFormula.exception };
            calculationExceptions.push(calculationException);
          }
        }
      }
    });
  }

  _calculateFormula(key, formula, attributeSelection) {
    try {
      const evaluatedFormula = Formula.evaluate(formula, attributeSelection);

      return new AttributeEvaluationResponse(key, true, false, {
        expression: formula,
        value: evaluatedFormula.toString(),
      });
    } catch (err) {
      /**
       * Ignore these two math-evaluation-helper exception types.
       * Invalid Context - indicates that the context contains invalid values.
       * Unknown String  - indicates that the expressions contain variables which do not exist within the context.
       */
      if (err.name === INVALID_CONTEXT_EXCEPTION_NAME || err.name === UNKNOWN_STRING_EXCEPTION_NAME) {
        return new AttributeEvaluationResponse(key, false, false, { expression: formula });
      }

      return new AttributeEvaluationResponse(key, false, true, { expression: formula }, new FormulaEvaluationException(err.message));
    }
  }

  _getSelectedAttributes(attributeDomain) {
    const selectedAttributes = {};
    _.forOwn(attributeDomain, (value, key) => {
      if (value.length === 1 && _isSpecificValue(value[0])) {
        [selectedAttributes[key.toLowerCase()]] = value;
      }
    });

    return selectedAttributes;
  }

  _getRulesUsingAttribute(attributeKey) {
    // when rule is not yet activated
    // when attr key is used anywhere
    const rulesOnAttribute = _.filter(this.rules, rule => !rule.active && this._doesRuleUseAttribute(rule, attributeKey));

    return rulesOnAttribute;
  }

  _doesRuleUseAttribute(rule, attributeKey) {
    return _.some(rule.ruleComponents, component => this._doesRuleComponentUseAttribute(component, attributeKey));
  }

  _doesRuleComponentUseAttribute(ruleComponent, attributeKey) {
    return _.some(ruleComponent.componentAttributes, attribute => attribute.key.toLowerCase() === attributeKey);
  }

  _applyActiveRule(attributeRule, attributeDomain, attributeRunList, calculationExceptions) {
    // apply exclusions
    _.each(attributeRule.ruleComponents, (ruleComponent) => {
      if (!ruleComponent.evaluateState) {
        _.each(ruleComponent.componentAttributes, (componentAttribute) => {
          this._applyActiveRuleComponentAttribute(componentAttribute, attributeDomain, attributeRunList);
        });
      }
    });

    this._implyRangeAttributes(attributeDomain);

    // decontainer, adding new attributes to the domain and enqueuing them onto the run list
    this._decontainerAttributeDomain(attributeDomain, attributeRunList);

    // calculate any calculable formula in the attribute domain.
    this._calculateAttributeDomainFormulas(attributeDomain, calculationExceptions);
  }

  _implyRangeAttributes(attributeDomain) {
    _.forOwn(attributeDomain, (values, key) => {
      if (this._isImpliableRangeValueSet(values)) {
        const impliableRange = values[0];
        const impliedRangeValue = impliableRange.minimum;

        // eslint-disable-next-line no-param-reassign
        attributeDomain[key] = [impliedRangeValue];
      }
    });
  }

  _isFormulaRuleComponentAttribute(componentAttribute) {
    return _.has(componentAttribute, 'formula') || _.has(componentAttribute, '_formula');
  }

  _getRuleComponentAttributeFormula(componentAttribute) {
    return _.has(componentAttribute, 'formula') ? componentAttribute.formula : componentAttribute._formula;
  }

  _applyActiveRuleComponentAttribute(componentAttribute, attributeDomain, attributeRunList) {
    const normalizedComponentAttributeKey = componentAttribute.key.toLowerCase();

    const componentAttributeValidOptions = [];
    if (this._isFormulaRuleComponentAttribute(componentAttribute)) {
      if (componentAttribute.canBeImplied()) {
        const validFormulaValue = componentAttribute.validOptions[0];
        const impliedFormulaValue = validFormulaValue.minimum;

        componentAttributeValidOptions.push(impliedFormulaValue.toString());
      } else {
        const formula = this._getRuleComponentAttributeFormula(componentAttribute);
        componentAttributeValidOptions.push(formula);
      }
    } else {
      componentAttributeValidOptions.push(...componentAttribute.validOptions);
    }

    const currentlyValidOptions = attributeDomain[normalizedComponentAttributeKey];

    if (componentAttribute.isValid) {
      if (_.isNil(currentlyValidOptions)) {
        // new attribute in the domain, enqueue it on the run list for later validation.
        attributeRunList.push(normalizedComponentAttributeKey);

        // eslint-disable-next-line no-param-reassign
        attributeDomain[normalizedComponentAttributeKey] = componentAttributeValidOptions;
      } else {
        // eslint-disable-next-line no-param-reassign
        attributeDomain[normalizedComponentAttributeKey] = _intersectValues(currentlyValidOptions, componentAttributeValidOptions);
      }
    } else if (!_.isNil(currentlyValidOptions)) {
      // eslint-disable-next-line no-param-reassign
      attributeDomain[normalizedComponentAttributeKey] = _excludeValues(currentlyValidOptions, componentAttributeValidOptions);
    }
  }

  _decontainerAttributeDomain(attributeDomain, attributeRunList) {
    _.forOwn(attributeDomain, (value, key) => {
      // only decontainer a selected attribute -- an attribute with a single valid value in the attribute domain.
      if (value.length === 1) {
        const decontaineredAttributeDomain = this._decontainerAttribute(key, value[0]);

        /**
         * listOfRanges attributes are constraints and should not be treated as listOfValues or formula
         * attributes unless it is a discrete selected value. This is because we do not yet support evaluating
         * range strings as we do value strings.
         * To work around this limitation, we only add listOfRanges attributes to the run list when the value is selected
         *
         * We may need to pull them out before evaluating rules on the attribute domain...
         */

        this._replaceUnspecifiedAttributeValues(attributeDomain, decontaineredAttributeDomain);

        attributeRunList.push(..._.keysIn(decontaineredAttributeDomain)); // enqueue all decontainered attribute selections for validation.
      }
    });
  }
};

/* eslint-enable no-underscore-dangle, class-methods-use-this */
